import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { User, MapPin, Trophy, Ruler, Weight, Calendar, Target, Camera, Image as ImageIcon } from 'lucide-react';
import TagInput from '../components/TagInput';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { userAPI } from '../services/api';
import { toast } from 'sonner';
import { getPositionsForSport, sportHasPositions } from '../config/sportPositions';

const EditProfile = () => {
  const navigate = useNavigate();

  // Convert any value to a clean string array for TagInput
  const toTagArray = (val) => {
    if (!val) return [];
    if (typeof val === 'string') return val.split('\n').map(s => s.trim()).filter(Boolean);
    if (!Array.isArray(val)) return [String(val)];
    return val.map(item => {
      if (typeof item === 'string') return item;
      if (typeof item === 'object' && item !== null) {
        // Handle various object shapes
        const parts = [item.title || item.name || item.event || ''];
        if (item.time) parts.push(item.time);
        if (item.date) parts.push(item.date);
        if (item.year) parts.push(`(${item.year})`);
        return parts.filter(Boolean).join(' ');
      }
      return String(item);
    }).filter(Boolean);
  };
  const [loading, setLoading] = useState(false);
  const [uploadingProfileImage, setUploadingProfileImage] = useState(false);
  const [uploadingCoverImage, setUploadingCoverImage] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    bio: '',
    organization: '',
    aboutMe: '',
    phone: '',
    contactEmail: '',
    website: '',
    location: '',
    sports: [],
    height: '',
    weight: '',
    dateOfBirth: '',
    age: '',
    position: '',
    speciality: '',
    profileImage: '',
    coverImage: '',
    persona: '',
    stats: {}
  });
  const [primarySport, setPrimarySport] = useState('');
  const [availablePositions, setAvailablePositions] = useState([]);

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    // Update available positions when primary sport changes
    if (primarySport && profile.sports.includes(primarySport)) {
      const positions = getPositionsForSport(primarySport);
      setAvailablePositions(positions);
    } else if (profile.sports.length > 0) {
      const positions = getPositionsForSport(profile.sports[0]);
      setAvailablePositions(positions);
      setPrimarySport(profile.sports[0]);
    }
  }, [primarySport, profile.sports]);

  const loadProfile = async () => {
    try {
      const response = await userAPI.getMyProfile();
      const userData = response.data;
      setProfile({
        name: userData.name || '',
        bio: userData.bio || '',
        organization: userData.organization || '',
        aboutMe: userData.aboutMe || '',
        phone: userData.phone || '',
        contactEmail: userData.contactEmail || '',
        website: userData.website || '',
        location: userData.location || '',
        sports: userData.sports || [],
        height: userData.height || '',
        weight: userData.weight || '',
        dateOfBirth: userData.dateOfBirth || '',
        age: userData.age || '',
        position: userData.position || '',
        speciality: userData.speciality || '',
        profileImage: userData.profileImage || '',
        coverImage: userData.coverImage || '',
        persona: userData.persona || '',
        stats: userData.stats || {}
      });
      
      if (userData.sports && userData.sports.length > 0) {
        setPrimarySport(userData.sports[0]);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
    
    // Calculate age if date of birth changes
    if (name === 'dateOfBirth' && value) {
      const age = calculateAge(value);
      setProfile(prev => ({ ...prev, age }));
    }
  };

  const handleSportsChange = (e) => {
    const sportsString = e.target.value;
    // Split, trim, filter empty, and remove duplicates
    const sportsArray = [...new Set(
      sportsString.split(',').map(s => s.trim()).filter(s => s)
    )];
    setProfile(prev => ({ ...prev, sports: sportsArray }));
    
    // Update primary sport if sports array changes
    if (sportsArray.length > 0 && !sportsArray.includes(primarySport)) {
      setPrimarySport(sportsArray[0]);
    }
  };

  const handleStatsChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      stats: {
        ...prev.stats,
        [field]: value
      }
    }));
  };

  const calculateAge = (dateString) => {
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingProfileImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('imageType', 'profile');

      const response = await userAPI.uploadProfileImage(formData);
      const imageUrl = response.data.imageUrl;

      setProfile(prev => ({ ...prev, profileImage: imageUrl }));
      
      // Update localStorage immediately
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.profileImage = imageUrl;
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success('Profile picture updated!');
    } catch (error) {
      console.error('Error uploading profile image:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload profile picture';
      toast.error(errorMsg);
    } finally {
      setUploadingProfileImage(false);
    }
  };

  const handleCoverImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingCoverImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('imageType', 'cover');

      const response = await userAPI.uploadProfileImage(formData);
      const imageUrl = response.data.imageUrl;

      setProfile(prev => ({ ...prev, coverImage: imageUrl }));
      
      // Update localStorage immediately
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.coverImage = imageUrl;
      localStorage.setItem('user', JSON.stringify(user));
      
      toast.success('Cover image updated!');
    } catch (error) {
      console.error('Error uploading cover image:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload cover image';
      toast.error(errorMsg);
    } finally {
      setUploadingCoverImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('Submitting profile data:', profile);
      const response = await userAPI.updateProfile(profile);
      console.log('Profile update response:', response.data);
      
      toast.success('Profile updated successfully!');
      
      // Update localStorage with all profile data
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = { ...user, ...profile };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      navigate('/profile/' + user.username);
    } catch (error) {
      console.error('Error updating profile:', error);
      console.error('Error response:', error.response?.data);
      
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to update profile';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-20">
        <div className="py-6">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-foreground">Edit Profile</h1>
            <p className="text-muted-foreground">Update your personal information and stats</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Images - Modern UI */}
            <Card className="overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center">
                  <Camera className="w-5 h-5 mr-2 text-primary" />
                  Profile Images
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6 p-0">
                {/* Cover Image - Full Width Interactive */}
                <div className="relative group">
                  <div 
                    className="w-full h-48 md:h-56 bg-cover bg-center transition-all duration-300 cursor-pointer"
                    style={{
                      backgroundImage: profile.coverImage 
                        ? `url(${profile.coverImage})`
                        : 'linear-gradient(135deg, #8854C8 0%, #6B3FA0 100%)'
                    }}
                    onClick={() => document.getElementById('coverImage').click()}
                  >
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                      <div className="text-center text-white transform scale-90 group-hover:scale-100 transition-transform duration-300">
                        {uploadingCoverImage ? (
                          <div className="flex flex-col items-center">
                            <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                            <p className="text-sm font-medium">Uploading...</p>
                          </div>
                        ) : (
                          <>
                            <ImageIcon className="w-10 h-10 mx-auto mb-2" />
                            <p className="text-sm font-medium">Click to change cover image</p>
                            <p className="text-xs text-white/70 mt-1">Recommended: 1920 x 480px</p>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Edit badge */}
                    <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Camera className="w-4 h-4 text-primary" />
                    </div>
                  </div>
                  
                  <input
                    type="file"
                    id="coverImage"
                    accept="image/*"
                    onChange={handleCoverImageUpload}
                    className="hidden"
                  />
                  
                  {/* Profile Picture - Overlapping */}
                  <div className="absolute -bottom-16 left-6 md:left-8">
                    <div 
                      className="relative group/avatar cursor-pointer"
                      onClick={() => document.getElementById('profileImage').click()}
                    >
                      <Avatar className="w-28 h-28 md:w-32 md:h-32 border-4 border-background shadow-xl transition-transform duration-300 group-hover/avatar:scale-105">
                        <AvatarImage src={profile.profileImage} alt="Profile" className="object-cover" />
                        <AvatarFallback className="text-3xl bg-gradient-to-br from-primary to-purple-600 text-white">
                          {profile.name?.[0] || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover/avatar:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                        {uploadingProfileImage ? (
                          <div className="w-8 h-8 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Camera className="w-6 h-6 text-white" />
                        )}
                      </div>
                      
                      {/* Edit badge */}
                      <div className="absolute -bottom-1 -right-1 bg-primary rounded-full p-2 shadow-lg border-2 border-background">
                        <Camera className="w-3.5 h-3.5 text-white" />
                      </div>
                    </div>
                    
                    <input
                      type="file"
                      id="profileImage"
                      accept="image/*"
                      onChange={handleProfileImageUpload}
                      className="hidden"
                    />
                  </div>
                </div>
                
                {/* Spacer for overlapping avatar */}
                <div className="h-16"></div>
                
                {/* Tips section */}
                <div className="px-6 pb-6">
                  <div className="bg-muted/50 rounded-lg p-4">
                    <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">💡 Tip:</span> Click on the images above to upload. Use high-quality images for the best results. 
                      Profile picture will be displayed across the platform.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2 text-primary" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={profile.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <Label htmlFor="bio">Bio (Short description for profile card)</Label>
                  <Textarea
                    id="bio"
                    name="bio"
                    value={profile.bio}
                    onChange={handleChange}
                    placeholder="Professional athlete | Football enthusiast"
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="organization">Organization / Company (Optional)</Label>
                  <Input
                    id="organization"
                    name="organization"
                    value={profile.organization}
                    onChange={handleChange}
                    placeholder="e.g., Manchester United FC, ABC Sports Academy"
                  />
                </div>

                <div>
                  <Label htmlFor="aboutMe">About Me (Detailed information for About tab)</Label>
                  <Textarea
                    id="aboutMe"
                    name="aboutMe"
                    value={profile.aboutMe}
                    onChange={handleChange}
                    placeholder="Tell us more about yourself, your journey, achievements, goals..."
                    rows={5}
                  />
                </div>

                <div>
                  <Label htmlFor="location">Location</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="location"
                      name="location"
                      value={profile.location}
                      onChange={handleChange}
                      placeholder="London, UK"
                      className="pl-10"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2 text-primary" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="phone">Phone (Optional)</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={profile.phone}
                    onChange={handleChange}
                    placeholder="+44 7700 900000"
                  />
                </div>

                <div>
                  <Label htmlFor="contactEmail">Email (Optional)</Label>
                  <Input
                    id="contactEmail"
                    name="contactEmail"
                    type="email"
                    value={profile.contactEmail}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="website">Website (Optional)</Label>
                  <Input
                    id="website"
                    name="website"
                    type="url"
                    value={profile.website}
                    onChange={handleChange}
                    placeholder="https://yourwebsite.com"
                  />
                </div>

                <div>
                  <Label htmlFor="sports">Sports</Label>
                  <TagInput
                    tags={profile.sports}
                    onChange={(newSports) => {
                      setProfile(prev => ({ ...prev, sports: newSports }));
                      if (newSports.length > 0 && !newSports.includes(primarySport)) {
                        setPrimarySport(newSports[0]);
                      }
                    }}
                    placeholder="e.g. Football"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Physical Stats - Only for Athletes */}
            {profile.persona === 'athlete' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Ruler className="w-5 h-5 mr-2 text-primary" />
                    Physical Stats
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="height">Height</Label>
                      <div className="relative">
                        <Ruler className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="height"
                          name="height"
                          value={profile.height}
                          onChange={handleChange}
                          placeholder="180 cm or 5'11&quot;"
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="weight">Weight</Label>
                      <div className="relative">
                        <Weight className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="weight"
                          name="weight"
                          value={profile.weight}
                          onChange={handleChange}
                          placeholder="75 kg or 165 lbs"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="dateOfBirth">Date of Birth</Label>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="dateOfBirth"
                          name="dateOfBirth"
                          type="date"
                          value={profile.dateOfBirth}
                          onChange={handleChange}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="age">Age</Label>
                      <Input
                        id="age"
                        name="age"
                        type="number"
                        value={profile.age}
                        readOnly
                        placeholder="Calculated from DOB"
                        className="bg-secondary"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Position/Speciality - Only for Athletes */}
            {profile.persona === 'athlete' && profile.sports.length > 0 && sportHasPositions(primarySport) && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Target className="w-5 h-5 mr-2 text-primary" />
                    Position / Speciality
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {profile.sports.length > 1 && (
                    <div>
                      <Label htmlFor="primarySport">Primary Sport for Position</Label>
                      <select
                        id="primarySport"
                        value={primarySport}
                        onChange={(e) => setPrimarySport(e.target.value)}
                        className="w-full p-2 border rounded-md bg-background text-foreground border-border"
                      >
                        {profile.sports.map(sport => (
                          <option key={sport} value={sport} className="bg-background text-foreground">{sport}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div>
                    <Label htmlFor="position">Position for {primarySport}</Label>
                    <select
                      id="position"
                      name="position"
                      value={profile.position}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-md bg-background text-foreground border-border"
                    >
                      <option value="" className="bg-background text-foreground">Select Position</option>
                      {availablePositions.map(pos => (
                        <option key={pos} value={pos} className="bg-background text-foreground">{pos}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="speciality">Additional Speciality (Optional)</Label>
                    <Input
                      id="speciality"
                      name="speciality"
                      value={profile.speciality}
                      onChange={handleChange}
                      placeholder="e.g., Free kicks specialist, Set pieces"
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Stats & Achievements */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Trophy className="w-5 h-5 mr-2 text-primary" />
                  Stats & Achievements
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {profile.persona === 'athlete' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="competitions">Competitions</Label>
                        <Input
                          id="competitions"
                          type="number"
                          value={profile.stats?.competitions || ''}
                          onChange={(e) => handleStatsChange('competitions', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label htmlFor="wins">Wins</Label>
                        <Input
                          id="wins"
                          type="number"
                          value={profile.stats?.wins || ''}
                          onChange={(e) => handleStatsChange('wins', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="personalBests">Personal Bests</Label>
                      <TagInput
                        tags={toTagArray(profile.stats?.personalBests)}
                        onChange={(val) => handleStatsChange('personalBests', val)}
                        placeholder="e.g. 100m: 10.2s"
                      />
                    </div>
                  </>
                )}

                {profile.persona === 'coach' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="yearsExperience">Years Experience</Label>
                        <Input
                          id="yearsExperience"
                          type="number"
                          value={profile.stats?.yearsExperience || ''}
                          onChange={(e) => handleStatsChange('yearsExperience', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label htmlFor="athletesTrained">Athletes Trained</Label>
                        <Input
                          id="athletesTrained"
                          type="number"
                          value={profile.stats?.athletesTrained || ''}
                          onChange={(e) => handleStatsChange('athletesTrained', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label htmlFor="championships">Championships</Label>
                        <Input
                          id="championships"
                          type="number"
                          value={profile.stats?.championships || ''}
                          onChange={(e) => handleStatsChange('championships', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="philosophy">Coaching Philosophy</Label>
                      <Textarea
                        id="philosophy"
                        value={profile.stats?.philosophy || ''}
                        onChange={(e) => handleStatsChange('philosophy', e.target.value)}
                        placeholder="Describe your coaching philosophy..."
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label htmlFor="certifications">Certifications</Label>
                      <TagInput
                        tags={toTagArray(profile.stats?.certifications)}
                        onChange={(val) => handleStatsChange('certifications', val)}
                        placeholder="e.g. UEFA Pro License"
                      />
                    </div>
                  </>
                )}

                {profile.persona === 'club' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="founded">Founded Year</Label>
                        <Input
                          id="founded"
                          type="number"
                          value={profile.stats?.founded || ''}
                          onChange={(e) => handleStatsChange('founded', parseInt(e.target.value) || 0)}
                          placeholder="2000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="members">Members</Label>
                        <Input
                          id="members"
                          type="number"
                          value={profile.stats?.members || ''}
                          onChange={(e) => handleStatsChange('members', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label htmlFor="trophies">Trophies</Label>
                        <Input
                          id="trophies"
                          type="number"
                          value={profile.stats?.trophies || ''}
                          onChange={(e) => handleStatsChange('trophies', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="facilities">Facilities</Label>
                      <TagInput
                        tags={toTagArray(profile.stats?.facilities)}
                        onChange={(val) => handleStatsChange('facilities', val)}
                        placeholder="e.g. Full-size pitch"
                      />
                    </div>
                    <div>
                      <Label htmlFor="achievements">Achievements</Label>
                      <TagInput
                        tags={toTagArray(profile.stats?.achievements)}
                        onChange={(val) => handleStatsChange('achievements', val)}
                        placeholder="e.g. Premier League 2024"
                      />
                    </div>
                  </>
                )}

                {profile.persona === 'nutritionist' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="yearsExperience">Years Experience</Label>
                        <Input
                          id="yearsExperience"
                          type="number"
                          value={profile.stats?.yearsExperience || ''}
                          onChange={(e) => handleStatsChange('yearsExperience', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label htmlFor="clientsHelped">Clients Helped</Label>
                        <Input
                          id="clientsHelped"
                          type="number"
                          value={profile.stats?.clientsHelped || ''}
                          onChange={(e) => handleStatsChange('clientsHelped', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="specializations">Specializations</Label>
                      <TagInput
                        tags={toTagArray(profile.stats?.specializations)}
                        onChange={(val) => handleStatsChange('specializations', val)}
                        placeholder="e.g. Performance nutrition"
                      />
                    </div>
                    <div>
                      <Label htmlFor="certifications">Certifications</Label>
                      <TagInput
                        tags={toTagArray(profile.stats?.certifications)}
                        onChange={(val) => handleStatsChange('certifications', val)}
                        placeholder="e.g. Certified Sports Nutritionist"
                      />
                    </div>
                    <div>
                      <Label htmlFor="qualifications">Qualifications</Label>
                      <TagInput
                        tags={toTagArray(profile.stats?.qualifications)}
                        onChange={(val) => handleStatsChange('qualifications', val)}
                        placeholder="e.g. PhD Sports Nutrition"
                      />
                    </div>
                  </>
                )}

                {profile.persona === 'physio' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="yearsExperience">Years Experience</Label>
                        <Input
                          id="yearsExperience"
                          type="number"
                          value={profile.stats?.yearsExperience || ''}
                          onChange={(e) => handleStatsChange('yearsExperience', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                      <div>
                        <Label htmlFor="athletesTreated">Athletes Treated</Label>
                        <Input
                          id="athletesTreated"
                          type="number"
                          value={profile.stats?.athletesTreated || ''}
                          onChange={(e) => handleStatsChange('athletesTreated', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="specializations">Specializations</Label>
                      <TagInput
                        tags={toTagArray(profile.stats?.specializations)}
                        onChange={(val) => handleStatsChange('specializations', val)}
                        placeholder="e.g. ACL reconstruction rehab"
                      />
                    </div>
                    <div>
                      <Label htmlFor="certifications">Certifications</Label>
                      <TagInput
                        tags={toTagArray(profile.stats?.certifications)}
                        onChange={(val) => handleStatsChange('certifications', val)}
                        placeholder="e.g. Chartered Physiotherapist"
                      />
                    </div>
                    <div>
                      <Label htmlFor="qualifications">Qualifications</Label>
                      <TagInput
                        tags={toTagArray(profile.stats?.qualifications)}
                        onChange={(val) => handleStatsChange('qualifications', val)}
                        placeholder="e.g. MSc Sports Physiotherapy"
                      />
                    </div>
                  </>
                )}

                {profile.persona === 'fan' && (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="favoriteTeams">Favorite Teams</Label>
                        <TagInput
                          tags={toTagArray(profile.stats?.favoriteTeams)}
                          onChange={(val) => handleStatsChange('favoriteTeams', val)}
                          placeholder="e.g. Manchester United"
                        />
                      </div>
                      <div>
                        <Label htmlFor="eventsAttended">Events Attended</Label>
                        <Input
                          id="eventsAttended"
                          type="number"
                          value={profile.stats?.eventsAttended || ''}
                          onChange={(e) => handleStatsChange('eventsAttended', parseInt(e.target.value) || 0)}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  </>
                )}

                {!profile.persona && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>Stats fields will appear based on your persona type.</p>
                    <p className="text-sm mt-2">Update your profile to add persona-specific stats.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex space-x-4">
              <Button
                type="submit"
                className="flex-1 text-white"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
